import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useRouteMeta } from "@/lib/seo";
import { authService } from "@/services/authService";
import { toast } from "sonner";

function AuthShell({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 shadow-editorial">
      <h1 className="text-display text-3xl uppercase text-navy">{title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      <div className="mt-6">{children}</div>
      {footer ? <div className="mt-6 text-sm text-muted-foreground">{footer}</div> : null}
    </div>
  );
}

export function ForgotPasswordPage() {
  useRouteMeta({ title: "Forgot password — Soweto Super United FC", noindex: true });
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await authService.forgotPassword(email);
    } catch {
      /* mock adapter tolerates */
    }
    setSent(true);
    toast.success("If the address exists, a reset link has been sent.");
  }
  return (
    <AuthShell
      title="Forgot password"
      description="Enter the email on your account and we'll send a reset link."
    >
      {sent ? (
        <p className="rounded-sm border border-success/30 bg-success/10 p-4 text-sm text-navy">
          Check your inbox for a password-reset link.
        </p>
      ) : (
        <form onSubmit={submit} className="grid gap-3">
          <label className="grid gap-1 text-sm">
            <span className="text-xs font-semibold uppercase tracking-[0.14em]">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-h-11 rounded-sm border border-input px-3"
              autoComplete="email"
            />
          </label>
          <button className="min-h-11 rounded-sm bg-navy text-sm font-bold uppercase tracking-[0.16em] text-white">
            Send reset link
          </button>
        </form>
      )}
      <div className="mt-4 flex justify-between text-sm">
        <Link to="/auth/login" className="text-navy hover:underline">
          Back to sign in
        </Link>
        <Link to="/" className="text-muted-foreground hover:underline">
          Back to site
        </Link>
      </div>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  useRouteMeta({ title: "Reset password — Soweto Super United FC", noindex: true });
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const email = params.get("email") ?? "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email) return toast.error("This reset link is missing the account email.");
    if (pw.length < 12) return toast.error("Password must be at least 12 characters.");
    if (pw !== confirm) return toast.error("Passwords do not match.");
    try {
      await authService.resetPassword(token, email, pw, confirm);
    } catch {
      /* mock */
    }
    setDone(true);
    toast.success("Password reset.");
  }
  return (
    <AuthShell
      title="Reset password"
      description="Choose a new password. You'll be signed in afterwards."
    >
      {done ? (
        <p className="rounded-sm border border-success/30 bg-success/10 p-4 text-sm text-navy">
          Password updated.{" "}
          <Link to="/auth/login" className="underline">
            Sign in →
          </Link>
        </p>
      ) : (
        <form onSubmit={submit} className="grid gap-3">
          <label className="grid gap-1 text-sm">
            <span className="text-xs font-semibold uppercase tracking-[0.14em]">New password</span>
            <input
              type="password"
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="min-h-11 rounded-sm border border-input px-3"
              autoComplete="new-password"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-xs font-semibold uppercase tracking-[0.14em]">
              Confirm password
            </span>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="min-h-11 rounded-sm border border-input px-3"
              autoComplete="new-password"
            />
          </label>
          <button className="min-h-11 rounded-sm bg-navy text-sm font-bold uppercase tracking-[0.16em] text-white">
            Reset password
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  useRouteMeta({ title: "Verify email — Soweto Super United FC", noindex: true });
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const hasToken = !!token;
  const [status, setStatus] = useState<"idle" | "verifying" | "verified" | "error">(
    hasToken ? "verifying" : "idle",
  );

  useEffect(() => {
    if (!token) return;
    authService
      .verifyEmail(token)
      .then(() => setStatus("verified"))
      .catch(() => setStatus("error"));
  }, [token]);

  async function resend() {
    try {
      await authService.resendVerification();
      toast.success("Verification email sent.");
    } catch {
      toast.error("Sign in to request another verification email.");
    }
  }
  return (
    <AuthShell
      title="Verify email"
      description={
        hasToken
          ? "Confirming your email address."
          : "We've sent a verification link to your inbox."
      }
    >
      {status === "verifying" ? (
        <p className="rounded-sm border border-border p-4 text-sm text-navy">
          Verifying your email address…
        </p>
      ) : status === "error" ? (
        <p className="rounded-sm border border-live/30 bg-live/10 p-4 text-sm text-navy">
          This verification link is invalid or has expired.
        </p>
      ) : status === "verified" ? (
        <p className="rounded-sm border border-success/30 bg-success/10 p-4 text-sm text-navy">
          Email verified.{" "}
          <Link to="/auth/login" className="underline">
            Sign in →
          </Link>
        </p>
      ) : (
        <div className="grid gap-3">
          <p className="text-sm text-slate-brand">
            Didn't get it? Check your spam folder, or request another link.
          </p>
          <button
            onClick={resend}
            className="min-h-11 rounded-sm bg-navy text-sm font-bold uppercase tracking-[0.16em] text-white"
          >
            Resend email
          </button>
        </div>
      )}
      <div className="mt-4">
        <Link to="/auth/login" className="text-sm text-navy hover:underline">
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
}
